import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import { getTourDetails, setSiteVisited } from '../../../../../api/MyTours';
import { useFetch } from '../../../../../hooks/useFetch';
import FormValidationDisplay from '../../../../common/form/ValidationDisplay';
import LoadingOverlay from '../../../../common/LoadingOverlay';
import '../../../../../styles/StartTourPage.css';
import IconButton from '../../../../common/form/IconButton';

import AccessIcon from '../../../../../images/access_icon.png';
import MapIcon from '../../../../../images/map_icon.png';
import FaqIcon from '../../../../../images/ask_icon.png';
import ApplyIcon from '../../../../../images/apply_icon.png';
import { browserHistory } from '../../../../../utilities/history';
import { prospectPages, prospectPath } from '../../ProspectRouter';
import { useEffect, useState } from 'react';
import TourAccessPopup from './TourAccessPopup';
import * as moment from 'moment';
import { intersperse } from '../../../../../utilities/intersperse';
import { getAmazonKeys, sendCordinatesAmazon, unlockLocAmazon, 
    unlockStatusLocAmazon 
} from '../../../../../api/Prospect';
import { Modal, ModalBody, ModalFooter, Button } from 'reactstrap';

export interface Props extends RouteComponentProps<{ tourGuid: string }> {}

const StartTourPage = (props: Props) => {
    const MinutesBeforeTourStart = 10;
    const [accessWindowIsActive, setAccessWindowIsActive] = useState(false);

    const {
        data: tourAccessData,
        loading: isLoading,
        error: errorMessage
    } = useFetch(() => getTourDetails(props.match.params.tourGuid), [], undefined);
    sessionStorage.setItem('tourGuid',props.match.params.tourGuid);
    const [tourAccessPopupIsOpen, setTourAccessPopupIsOpen] = useState(false);
    const [loadAmzonStatus, setLoadAmazonStatus] = useState(false);
    const [selectedAmazonLock, setSelectedAmazonLock] = useState<any>();

    const [keyLocations, setKeyLocations] = useState<any>([])
    const [locationError, setLocationError] = useState<string | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [viewAmazonInstructions, setViewAmazonInstructions] = useState(false);
    const [userLocation, setUserLocation] = useState<{ latitude: number, longitude: number } | null>(null);
    const [unlockStatus, setUnlockStatus] = useState('')
    const [unlockSuccessStatus, setUnlockSuccessStatus] = useState('')
    const amazonLockErr = 'There is an issue, please contact the property team in order to continue the tour.'


    const getUserLocation = (): Promise<{ latitude: number, longitude: number }> => {
        return new Promise((resolve, reject) => {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        resolve({
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude
                        });
                    },
                    (error) => reject(error),
                    { enableHighAccuracy: true }
                );
            } else {
                reject(new Error('Geolocation is not supported by this browser.'));
            }
        });
    };

    const toggleModal = () => {
        setModalOpen(!modalOpen);
    };

    const toggleAmazon = () => {
        setUnlockStatus('')
        setUnlockSuccessStatus('')
        setViewAmazonInstructions(!viewAmazonInstructions);
    };

    const handleAccessClick = () => {
        setTourAccessPopupIsOpen(true);
        return;
    };
    const handleMapClick = () => {
        if (!tourAccessData) {
            return;
        }
        if (tourAccessData.hasUnitMap) {
            browserHistory.push(`${prospectPath(prospectPages.unitmap)}/${tourAccessData.urlSlug}`);
            return;
        }
        browserHistory.push(`${prospectPath(prospectPages.propertyMap)}/${tourAccessData.urlSlug}`);
        return;
    };
    const handleAskClick = () => {
        if (!tourAccessData) {
            return;
        }
        browserHistory.push(`${prospectPath(prospectPages.ask)}/${tourAccessData.urlSlug}`);
    };
    const handleApplyClick = () => {
        if (tourAccessData && tourAccessData.leasingUrl) {
            window.location.href = tourAccessData.leasingUrl;
        }
    };

    const checkAccessTime = () => {
        if (!tourAccessData || !tourAccessData.tourStart) {
            return;
        }
        let currentTime = moment(new Date());

        let accessStart = moment
            .utc(tourAccessData.tourStart)
            .subtract(MinutesBeforeTourStart, 'minutes');
        let accessEnd = moment.utc(tourAccessData.tourEnd);
        //Adding Z to time strings to properly format as UTC time
        if (currentTime < accessStart || currentTime > accessEnd) {
            setAccessWindowIsActive(false);
            return;
        }
        setAccessWindowIsActive(true);
    };

    const fetchAmazonData = async () =>{
        if(tourAccessData && tourAccessData.isAmazonKeyEnabled){

            let getKeys = await getAmazonKeys(props.match.params.tourGuid);
            if (getKeys && getKeys.success && getKeys.data) {
                setKeyLocations(getKeys.data);
            }
        }
    }

    const checkUnlockStatus = async (
        awsPreCheck: any,
        tourGuid: any,
        attempts: number = 0,
        maxAttempts: number = 15,
        interval: number = 2000
    ) => {
        try {
            if (attempts >= maxAttempts) {
                console.log('Max attempts reached. Stopping polling.');
                setUnlockStatus('There is an issue, please contact the property team in order to continue the tour.');
                setLoadAmazonStatus(false);
                return;
            }
    
            const unlockStatus = await unlockStatusLocAmazon(awsPreCheck, tourGuid);
    
            if (unlockStatus && unlockStatus.success && unlockStatus.data) {
                if (
                    unlockStatus.data.actionStatus &&
                    unlockStatus.data.actionStatus.status.toLowerCase() === 'success'
                ) {
                    console.log('Unlock successful:', unlockStatus);
                    setLoadAmazonStatus(false);
                    setUnlockSuccessStatus('Unlock Success')
                } else {
                    console.log('Unlock status not success yet, retrying...');
                    setTimeout(() => {
                        checkUnlockStatus(awsPreCheck, tourGuid, attempts + 1, maxAttempts, interval);
                    }, interval);
                }
            } else {
                setLoadAmazonStatus(false);
                setUnlockStatus(unlockStatus.error || 'There is an issue, please contact the property team in order to continue the tour.');
            }
        } catch (error) {
            setLoadAmazonStatus(false);
            setUnlockStatus('There is an issue, please contact the property team in order to continue the tour.');
        }
    };
    

    const sendCordinates = async (key: string) => {
        if (key) {
            try {
                setUnlockStatus('')
                setUnlockSuccessStatus('')
                if(userLocation){
                    const latitude = userLocation.latitude;
                    const longitude = userLocation.longitude;
                    let preCheckData = await sendCordinatesAmazon({
                        latitude,
                        longitude,
                        accessPointId: key
                    }, props.match.params.tourGuid);
                    
                    if (preCheckData && preCheckData.success && preCheckData.data) {
    
                        let unlockData = await unlockLocAmazon(preCheckData.data.awsPreCheck, props.match.params.tourGuid);
                        if (unlockData && unlockData.success && unlockData.data) {
                           checkUnlockStatus(preCheckData.data.awsPreCheck, props.match.params.tourGuid)
                        }else{
                            setLoadAmazonStatus(false)
                            setUnlockStatus(unlockData.error||amazonLockErr)
                        }
                    }else{
                        setLoadAmazonStatus(false)
                        setUnlockStatus(preCheckData.error||amazonLockErr)
                    }
                }
            } catch (error) {
                console.error('Error fetching location', error);
            }
        }
    };

    useEffect(() => {
        const interval = setInterval(() => {
            checkAccessTime();
        }, 10000);
        return () => clearInterval(interval);
    }, []);

    const requestLocation = () => {
        getUserLocation()
            .then((location) => {
                setUserLocation(location);
                fetchAmazonData();
                setLocationError(null);
                setModalOpen(false); // Close modal after allowing location
            })
            .catch((error) => {
                setLocationError('In order to access location details, we need to get your location.');
                setModalOpen(true); // Open modal if location is denied
                console.error('Error getting location:', error);
            });
    };

    useEffect(() => {
        checkAccessTime();

        if (tourAccessData && tourAccessData.isAmazonKeyEnabled) {
            requestLocation();
        }
    }, [tourAccessData]);

    useEffect(() => {
        setSiteVisited(props.match.params.tourGuid);
    }, []);

    return (
        <Row className="tour-page">
            <Col size={12}>
                <LoadingOverlay show={isLoading} inBootstrapColumn={true} />
                <FormValidationDisplay errorMessage={errorMessage} />
                {tourAccessData && (
                    <div>
                        <TourAccessPopup
                            isOpen={tourAccessPopupIsOpen && accessWindowIsActive}
                            close={() => setTourAccessPopupIsOpen(false)}
                            accessInfo={tourAccessData}
                            keyLocations={keyLocations}
                            setViewAmazonInstructions={(value: boolean)=>{
                                setViewAmazonInstructions(value)
                            }}
                            setSelectedAmazonLock={(value: any)=>{
                                setSelectedAmazonLock(value)
                                setTourAccessPopupIsOpen(false)
                            }}
                        />
                        <div className="top-spacer" />
                        <label className="title center font-weight-bold">
                            {tourAccessData.propertyName}
                        </label>
                        {tourAccessData &&
                            //Try tour page specific image first
                            (tourAccessData.altPropertyImage ? (
                                <img
                                    src={tourAccessData.altPropertyImage}
                                    className="property-image"
                                />
                            ) : //fallback to property image
                            tourAccessData.propertyImage ? (
                                <img
                                    src={tourAccessData.propertyImage}
                                    className="property-image"
                                />
                            ) : (
                                //show nothing if no images exist
                                ' '
                            ))}

                        <Row className="justify-content-center icon-row m-2">
                            {accessWindowIsActive && (
                                <Col className="justify-content-center" align="center">
                                    <IconButton
                                        icon={AccessIcon}
                                        addlButtonClassName="icon mt-4"
                                        suppressBottomMargin={true}
                                        onClick={handleAccessClick}
                                    />
                                    <label className="title center font-weight-bold icon-label">
                                        ACCESS
                                    </label>
                                </Col>
                            )}
                            {(tourAccessData.mapUrl || tourAccessData.hasUnitMap) && (
                                <Col align="center">
                                    <IconButton
                                        icon={MapIcon}
                                        addlButtonClassName="icon mt-4"
                                        suppressBottomMargin={true}
                                        onClick={handleMapClick}
                                    />
                                    <label className="title center font-weight-bold icon-label">
                                        MAP
                                    </label>
                                </Col>
                            )}
                            <Col align="center">
                                <IconButton
                                    icon={FaqIcon}
                                    addlButtonClassName="icon mt-4"
                                    suppressBottomMargin={true}
                                    onClick={handleAskClick}
                                />
                                <label className="title center font-weight-bold icon-label">
                                    ASK
                                </label>
                            </Col>
                            <Col align="center">
                                <IconButton
                                    icon={ApplyIcon}
                                    addlButtonClassName="icon mt-4"
                                    suppressBottomMargin={true}
                                    onClick={handleApplyClick}
                                />
                                <label className="title center font-weight-bold icon-label">
                                    APPLY
                                </label>
                            </Col>
                        </Row>
                        <Modal isOpen={modalOpen} toggle={toggleModal}>
                            <LoadingOverlay show={loadAmzonStatus} inBootstrapColumn={false} />
                            <FormValidationDisplay errorMessage={errorMessage} />

                            <ModalBody>
                                <p>{locationError}</p>
                                <p>Please enable location access to proceed.</p>
                            </ModalBody>

                            <ModalFooter>
                                {/* <Button color="primary" onClick={requestLocation}>
                                    Allow Location Access
                                </Button> */}
                                <Button color="secondary" onClick={toggleModal}>
                                    Close
                                </Button>
                            </ModalFooter>
                        </Modal>
                        <Modal isOpen={viewAmazonInstructions} toggle={toggleAmazon}>
                            <LoadingOverlay show={loadAmzonStatus} inBootstrapColumn={false} />
                            <ModalBody>
                                <h2>Instructions</h2>
                                <p style={{ fontWeight: 'bold' }}>To unlock the door</p>
                                { selectedAmazonLock && <ul>Stay Near {selectedAmazonLock.name}</ul> }
                                <ul>Tap the unlock button.</ul>
                                { unlockStatus && <p style={{ color: 'red' }}>{unlockStatus}</p> }
                                { unlockSuccessStatus && <p style={{ color: 'blue' }}>{unlockSuccessStatus}</p> }
                            </ModalBody>

                            <ModalFooter>
                                <Button onClick={()=>{
                                    if(selectedAmazonLock){
                                        sendCordinates(selectedAmazonLock.key)
                                        setLoadAmazonStatus(true)
                                    }
                                }}>
                                    Unlock
                                </Button>
                                <Button color="secondary" onClick={toggleAmazon}>
                                    Close
                                </Button>
                            </ModalFooter>
                        </Modal>
                        <Row className="info m-2">
                            <div className="top-spacer" />
                            <label className="title font-weight-bold">INFO</label>
                            <p>
                                {tourAccessData.unitInstructions &&
                                    intersperse(
                                        tourAccessData.unitInstructions
                                            .split('\n')
                                            .map((line, index) => <span key={index}>{line}</span>),
                                        index => <br key={-index} />
                                    )}
                            </p>
                            <div className="top-spacer" />
                            <label className="title font-weight-bold">ACCESS</label>
                            {accessWindowIsActive ? (
                                <p>
                                    Tap the <b>ACCESS</b> icon above for your codes. Codes are only
                                    valid during your appointment.
                                </p>
                            ) : (
                                <p>
                                    Your Access codes will be available above 10 minutes before your
                                    tour. Codes are only valid during your appointment.
                                </p>
                            )}
                        </Row>
                    </div>
                )}
            </Col>
        </Row>
    );
};

export default StartTourPage;



// WEBPACK FOOTER //
// ./src/components/pages/prospect/tour/applessTour/StartTourPage.tsx